/**
 * Frontend client for the folder matcher.
 *
 * Companion to `irlMatchClient.ts` — that one matches per-file using a
 * content preview; this one matches per-folder using just the folder name
 * + a handful of sample file names inside. Catches the case where files
 * are opaquely named (Q1.xlsx, Q2.xlsx) but the folder name carries the
 * semantic ("AR Agings").
 *
 * Stateless: no cache, no batching. One Claude call per click — the input
 * is small (just folder names + IRL list) so caching adds little.
 */

import { isCodeExcluded } from "./folderTree";
import type { IRLItem } from "./types";
import { apiFetch } from "../../api/fddClient";

/** A folder we want Claude to consider. `code` is the hierarchical id from
 *  the project index (e.g., "1.2.3") and acts as the join key. */
export type FolderEntry = {
  code: string;
  name: string;
  /** Up to ~5 file names inside the folder, no content. */
  sampleFiles: string[];
};

export type FolderMatch = {
  number: string;
  confidence: "high" | "medium" | "low";
  reasoning: string;
};

export type FolderMatchResponse = {
  matches: Record<string, FolderMatch[]>;
};

/* ─────────────────────────── Cost estimate ─────────────────────────── */

// Sonnet 4.x list pricing — keep in sync with server/shared/claude.ts if the
// CLAUDE_MODEL changes. Cost figures here are "list" (no cache discount); the
// real bill is usually lower because the IRL list hits the prompt cache.
const PRICE_PER_M_INPUT_USD = 3.0;
const PRICE_PER_M_OUTPUT_USD = 15.0;

// Heuristic constants — chosen to over-estimate slightly so the user is never
// surprised by a higher actual bill.
const TOKENS_PER_CHAR = 0.25; // ~4 chars/token, English/JSON mix
const SCAFFOLD_INPUT_TOKENS = 600; // prompt boilerplate + JSON keys
const OUTPUT_TOKENS_PER_FOLDER = 70; // up to 3 matches × ~22 tokens

export type CostEstimate = {
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  folderCount: number;
};

export function estimateFolderMatchCost(args: {
  irlList: IRLItem[];
  folders: FolderEntry[];
}): CostEstimate {
  const irlChars = args.irlList.reduce(
    (sum, it) =>
      sum +
      it.number.length +
      it.request.length +
      (it.topic?.length ?? 0) +
      (it.entityName?.length ?? 0),
    0,
  );
  const folderChars = args.folders.reduce(
    (sum, f) =>
      sum +
      f.code.length +
      f.name.length +
      f.sampleFiles.reduce((s, n) => s + n.length, 0),
    0,
  );
  const inputTokens =
    Math.ceil((irlChars + folderChars) * TOKENS_PER_CHAR) + SCAFFOLD_INPUT_TOKENS;
  const outputTokens = args.folders.length * OUTPUT_TOKENS_PER_FOLDER;
  const costUsd =
    (inputTokens * PRICE_PER_M_INPUT_USD + outputTokens * PRICE_PER_M_OUTPUT_USD) /
    1_000_000;
  return { inputTokens, outputTokens, costUsd, folderCount: args.folders.length };
}

/** Format the cost as a short label for buttons / popovers. */
export function formatCostEstimate(est: CostEstimate): string {
  if (est.costUsd < 0.01) return "<$0.01";
  if (est.costUsd < 1) return `~$${est.costUsd.toFixed(2)}`;
  return `~$${est.costUsd.toFixed(1)}`;
}

/* ─────────────────────────── Folder catalog ─────────────────────────── */

/** Build the FolderEntry list from a flat array of (folderPath, code, file)
 *  tuples. Groups by code, picks the deepest folder segment as the display
 *  name, and trims to up to 5 sample file names per folder. Files whose code
 *  is excluded (matched by ancestor) are dropped — same exclusion semantics
 *  as the per-file matcher. Files with no code (root) are dropped entirely
 *  since there's no folder to attribute them to. */
export function buildFolderCatalog(args: {
  items: { folderPath: string; code: string | null; fileName: string }[];
  excludedFolders: Set<string>;
  maxSamplesPerFolder?: number;
}): FolderEntry[] {
  const max = args.maxSamplesPerFolder ?? 5;
  const grouped = new Map<string, { name: string; samples: string[] }>();
  for (const item of args.items) {
    if (!item.code) continue;
    if (isCodeExcluded(item.code, args.excludedFolders)) continue;
    const segs = item.folderPath.split("/").filter(Boolean);
    const name = segs[segs.length - 1] || item.code;
    const existing = grouped.get(item.code);
    if (existing) {
      if (existing.samples.length < max) existing.samples.push(item.fileName);
    } else {
      grouped.set(item.code, { name, samples: [item.fileName] });
    }
  }
  return Array.from(grouped.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([code, v]) => ({ code, name: v.name, sampleFiles: v.samples }));
}

/* ─────────────────────────── Network call ─────────────────────────── */

export async function runFolderMatch(args: {
  irlList: IRLItem[];
  folders: FolderEntry[];
}): Promise<Record<string, FolderMatch[]>> {
  const res = await apiFetch("/api/irl/folder-match", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(args),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error || `Folder match failed (HTTP ${res.status})`);
  }
  const data = (await res.json()) as FolderMatchResponse;
  return data.matches || {};
}
